import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, Users, Package, TrendingUp, MessageSquare, Crown, ArrowRight } from "lucide-react";

const StatCard = ({ label, value, icon: Icon, color, testid }) => (
  <div data-testid={testid} className="card-gc p-5 flex items-center gap-4">
    <div className={`w-12 h-12 rounded-xl ${color} text-white flex items-center justify-center flex-shrink-0`}>
      <Icon size={22} />
    </div>
    <div className="min-w-0">
      <div className="text-2xl font-bold text-stone-900">{value}</div>
      <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mt-0.5">{label}</div>
    </div>
  </div>
);

const Admin = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ prices: 0, listings: 0, schemes: 0, states: 0 });
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user || user.user_role !== "admin") {
      navigate("/");
      return;
    }
    Promise.all([
      api.get("/prices").then((r) => r.data.prices.length),
      api.get("/listings").then((r) => r.data.count),
      api.get("/schemes").then((r) => r.data.count),
      api.get("/prices/states").then((r) => r.data.states.length),
    ])
      .then(([prices, listings, schemes, states]) => {
        setStats({ prices, listings, schemes, states });
      })
      .finally(() => setStatsLoading(false));
  }, [user, loading, navigate]);

  if (loading || !user || user.user_role !== "admin") return null;

  return (
    <div data-testid="admin-page" className="container-gc px-4 sm:px-6 py-8 sm:py-12">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-12 h-12 rounded-2xl bg-[#1E4D2B] text-white flex items-center justify-center">
          <ShieldCheck size={22} />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl sm:text-4xl font-bold text-stone-900">Admin Console</h1>
            {user.is_demo && (
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#F59E0B] text-white">
                Demo Mode
              </span>
            )}
          </div>
          <p className="text-stone-600 text-sm mt-0.5">Full admin access · {user.email}</p>
        </div>
      </div>

      {statsLoading ? (
        <div className="card-gc p-12 text-center text-stone-500">Loading admin stats...</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard testid="admin-stat-prices" label="Mandi Prices" value={stats.prices} icon={TrendingUp} color="bg-[#1E4D2B]" />
          <StatCard testid="admin-stat-listings" label="Active Listings" value={stats.listings} icon={Package} color="bg-[#F59E0B]" />
          <StatCard testid="admin-stat-schemes" label="Schemes" value={stats.schemes} icon={Crown} color="bg-[#14361E]" />
          <StatCard testid="admin-stat-states" label="States Covered" value={stats.states} icon={Users} color="bg-[#D97706]" />
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-5">
        {[
          { title: "Manage Listings", desc: "View, moderate & remove garlic listings.", to: "/trade", icon: Package },
          { title: "Mandi Data", desc: "PAN-India mandi rates & filters.", to: "/mandi", icon: TrendingUp },
          { title: "Government Schemes", desc: "Curated central + state schemes.", to: "/schemes", icon: Crown },
          { title: "Contact Messages", desc: "Inbound queries from Contact form.", to: "/contact", icon: MessageSquare },
        ].map((it, i) => {
          const Icon = it.icon;
          return (
            <button
              key={i}
              onClick={() => navigate(it.to)}
              data-testid={`admin-quick-${i}`}
              className="card-gc p-5 flex items-center gap-4 hover:shadow-md hover:-translate-y-0.5 transition-all text-left"
            >
              <div className="w-11 h-11 rounded-xl bg-[#F4F1EA] text-[#1E4D2B] flex items-center justify-center flex-shrink-0">
                <Icon size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-stone-900">{it.title}</div>
                <div className="text-sm text-stone-600">{it.desc}</div>
              </div>
              <ArrowRight size={16} className="text-stone-400 flex-shrink-0" />
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default Admin;
